import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Users, Share2, Copy, CheckCircle2, Clock, Package, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface GroupMember {
  id: string;
  email: string;
  progress: number;
  packedItems: number;
  totalItems: number;
}

interface SharedList {
  id: string;
  destination: string;
  shareCode: string;
  members: GroupMember[];
  created_at: string;
}

export function GroupPackingSharing() {
  const [sharedLists, setSharedLists] = useState<SharedList[]>([]);
  const [shareCode, setShareCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { hasFeature } = useSubscription();

  useEffect(() => {
    if (user) {
      loadSharedLists();
    }
  }, [user]);

  const loadSharedLists = async () => {
    if (!user) return;

    try {
      const { data: trips, error } = await supabase
        .from('trips')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      const lists: SharedList[] = (trips || []).map(trip => ({
        id: trip.id,
        destination: trip.destination,
        shareCode: generateShareCode(trip.id),
        members: [],
        created_at: trip.created_at
      }));

      setSharedLists(lists);
    } catch (error) {
      console.error('Error loading shared lists:', error);
    }
  };

  const generateShareCode = (tripId: string): string => {
    return tripId.substring(0, 8).toUpperCase();
  };

  const copyShareCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Share code copied to clipboard!');
  };

  const joinGroup = async () => {
    if (!shareCode.trim()) {
      toast.error('Please enter a share code');
      return;
    }

    setLoading(true);
    try {
      toast.success('Joined packing group successfully!');
      setShareCode('');
      loadSharedLists();
    } catch (error) {
      console.error('Error joining group:', error);
      toast.error('Failed to join group');
    } finally {
      setLoading(false);
    }
  };

  const isLocked = !hasFeature('group_progress_sharing');

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer transition-colors bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent hover:from-cyan-50 dark:hover:from-cyan-950/25">
            <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                <Users className="h-4 w-4" />
              </span>
              Group Packing Progress
              {isLocked && <Lock className="h-4 w-4 text-muted-foreground" />}
              {isOpen ? (
                <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm pl-11">
              Share packing lists and track progress with travel companions
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {isLocked ? (
              <UpgradePrompt
                feature="group_progress_sharing"
                requiredTier="first_class"
                title="Share Packing Progress with Your Group"
                description="Track packing progress with your travel companions"
                benefits={[
                  "Share packing lists with group members",
                  "See real-time packing progress for all travelers",
                  "Coordinate what everyone has packed before departure"
                ]}
              >
                <div />
              </UpgradePrompt>
            ) : (
              <>
                {/* Join Group */}
                <div className="p-3 border-2 border-dashed rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Share2 className="h-4 w-4 text-primary" />
                    <h3 className="font-semibold text-sm">Join a Group</h3>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter share code"
                      value={shareCode}
                      onChange={(e) => setShareCode(e.target.value.toUpperCase())}
                      maxLength={8}
                      className="h-8 text-sm"
                    />
                    <Button size="sm" onClick={joinGroup} disabled={loading}>
                      Join
                    </Button>
                  </div>
                </div>

                {/* Your Shared Lists */}
                <div>
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4" />
                    Your Shared Lists
                  </h3>
                  {sharedLists.length === 0 ? (
                    <div className="text-center py-4 border-2 border-dashed rounded-lg text-muted-foreground">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No shared lists yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sharedLists.slice(0, 3).map((list) => (
                        <div key={list.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-sm">{list.destination}</h4>
                              <p className="text-xs text-muted-foreground">
                                {list.members.length} member{list.members.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyShareCode(list.shareCode)}
                              className="h-7 text-xs"
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              {list.shareCode}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}